export const DAY_CODES = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'] as const;

export type DayCode = (typeof DAY_CODES)[number];

export const BLOCK_TYPES = {
	AVAILABLE_GLOBAL: 'Available - Global',
	AVAILABLE_GROUP: 'Available - Group',
	AVAILABLE_CLIENT: 'Available - Client',
	BLOCK_GLOBAL: 'Blocked - Global',
	BLOCK_GROUP: 'Blocked - Group',
	BLOCK_CLIENT: 'Blocked - Client',
	MATCH_DERIVED: 'Match Derived'
} as const;

export type LegacyBlockType = keyof typeof BLOCK_TYPES;
export type BlockTypeLabel = (typeof BLOCK_TYPES)[LegacyBlockType];
export type AvailabilityBlockType = typeof BLOCK_TYPES.AVAILABLE_GLOBAL | typeof BLOCK_TYPES.AVAILABLE_GROUP | typeof BLOCK_TYPES.AVAILABLE_CLIENT;
export type BlockingBlockType = typeof BLOCK_TYPES.BLOCK_GLOBAL | typeof BLOCK_TYPES.BLOCK_GROUP | typeof BLOCK_TYPES.BLOCK_CLIENT | typeof BLOCK_TYPES.MATCH_DERIVED;
export type AvailabilityKind = 'available' | 'blocked' | 'match' | 'unknown';
export type ScopeKind = 'global' | 'group' | 'client';

export interface AvailabilityGroupRef {
	groupNumber: number;
	name?: string | null;
	shortName?: string | null;
}

export interface AvailabilityClientRef {
	clientNumber: number;
	name?: string | null;
}

export interface AvailabilityUserRef {
	userNumber: number;
	firstName?: string | null;
	lastName?: string | null;
}

export interface AvailabilityEntry {
	id?: number | null;
	blockType: BlockTypeLabel | null;
	startDate: string;
	endDate: string;
	startTime?: string | null;
	endTime?: string | null;
	allDay?: boolean;
	recurring?: boolean;
	daysOfWeek?: DayCode[] | null;
	notes?: string | null;
	group?: AvailabilityGroupRef | null;
	client?: AvailabilityClientRef | null;
	user?: AvailabilityUserRef | null;
	updatedBy?: AvailabilityUserRef | null;
	createdOn?: string | null;
	updatedOn?: string | null;
	source?: string | null;
	[key: string]: unknown;
}

export interface AvailabilitySuggestion {
	date: string;
	matchDay?: boolean;
	matchCount?: number;
	hasAvailability?: boolean;
	hasBlock?: boolean;
	matches?: unknown[];
	[key: string]: unknown;
}

export interface ScopedAvailabilityInput {
	date: string | Date;
	scope?: AvailabilityBlockType | LegacyBlockType | null;
	clientNumber?: number | null;
	groupNumber?: number | null;
	/**
	 * When supplied, blocking entries are evaluated against this match window
	 * instead of being treated as a whole-day restriction.
	 */
	startTime?: string | null;
	endTime?: string | null;
}

export interface ScopedAvailabilityStatus {
	date: string;
	hasAnyAvailability: boolean;
	hasScopedAvailability: boolean;
	hasBlockingEntry: boolean;
}

export interface AvailabilityQuickAddContext {
	scope: AvailabilityBlockType;
	groupNumber?: number | null;
	clientNumber?: number | null;
}

export interface AvailabilityCalendarOptions {
	initialDate?: Date | string | null;
	initialView?: 'month' | 'week' | 'agenda';
	selectedDates?: string[];
}

export interface AvailabilityPromptContext {
	mode?: 'assignment' | 'request';
	title?: string | null;
	message?: string | null;
	defaultQuickAdd?: AvailabilityQuickAddContext | null;
}

const LEGACY_BLOCK_TYPE_TO_LABEL: Record<LegacyBlockType, BlockTypeLabel> = {
	AVAILABLE_GLOBAL: BLOCK_TYPES.AVAILABLE_GLOBAL,
	AVAILABLE_GROUP: BLOCK_TYPES.AVAILABLE_GROUP,
	AVAILABLE_CLIENT: BLOCK_TYPES.AVAILABLE_CLIENT,
	BLOCK_GLOBAL: BLOCK_TYPES.BLOCK_GLOBAL,
	BLOCK_GROUP: BLOCK_TYPES.BLOCK_GROUP,
	BLOCK_CLIENT: BLOCK_TYPES.BLOCK_CLIENT,
	MATCH_DERIVED: BLOCK_TYPES.MATCH_DERIVED
};

const BLOCK_TYPE_LABELS = new Set<BlockTypeLabel>(Object.values(BLOCK_TYPES));

export function normalizeBlockType(value: unknown): BlockTypeLabel | null {
	if (typeof value !== 'string') return null;
	const trimmed = value.trim();
	if (!trimmed) return null;

	if (trimmed in LEGACY_BLOCK_TYPE_TO_LABEL) {
		return LEGACY_BLOCK_TYPE_TO_LABEL[trimmed as LegacyBlockType];
	}

	return BLOCK_TYPE_LABELS.has(trimmed as BlockTypeLabel) ? (trimmed as BlockTypeLabel) : null;
}

export function getAvailabilityKind(value: unknown): AvailabilityKind {
	const blockType = normalizeBlockType(value);
	if (!blockType) return 'unknown';
	if (blockType === BLOCK_TYPES.MATCH_DERIVED) return 'match';
	if (blockType.startsWith('Available - ')) return 'available';
	if (blockType.startsWith('Blocked - ')) return 'blocked';
	return 'unknown';
}

export function isAvailabilityBlockType(value: unknown): value is AvailabilityBlockType {
	return getAvailabilityKind(value) === 'available';
}

export function isBlockingBlockType(value: unknown): value is BlockingBlockType {
	const kind = getAvailabilityKind(value);
	return kind === 'blocked' || kind === 'match';
}

export function getAvailabilityBlockTypeForScope(scope: ScopeKind): AvailabilityBlockType {
	if (scope === 'client') return BLOCK_TYPES.AVAILABLE_CLIENT;
	if (scope === 'group') return BLOCK_TYPES.AVAILABLE_GROUP;
	return BLOCK_TYPES.AVAILABLE_GLOBAL;
}

export function getBlockingBlockTypeForScope(scope: ScopeKind): BlockingBlockType {
	if (scope === 'client') return BLOCK_TYPES.BLOCK_CLIENT;
	if (scope === 'group') return BLOCK_TYPES.BLOCK_GROUP;
	return BLOCK_TYPES.BLOCK_GLOBAL;
}

export function normalizeDayCodes(value: unknown): DayCode[] {
	if (Array.isArray(value)) {
		return value.map((entry) => String(entry).trim().toUpperCase()).filter((entry): entry is DayCode => DAY_CODES.includes(entry as DayCode));
	}

	if (typeof value === 'string') {
		return value
			.split(',')
			.map((entry) => entry.trim().toUpperCase())
			.filter((entry): entry is DayCode => DAY_CODES.includes(entry as DayCode));
	}

	return [];
}

export function normalizeAvailabilityEntry<T extends Record<string, unknown>>(entry: T): AvailabilityEntry & T {
	const normalizedEntry = entry as AvailabilityEntry & T;
	return {
		...normalizedEntry,
		blockType: normalizeBlockType(entry['blockType']),
		daysOfWeek: normalizeDayCodes(entry['daysOfWeek'])
	};
}

export function normalizeAvailabilityEntries<T extends Record<string, unknown>>(entries: T[] | null | undefined): Array<AvailabilityEntry & T> {
	if (!Array.isArray(entries)) return [];
	return entries.map((entry) => normalizeAvailabilityEntry(entry));
}

export function scopesMatchEntry(
	entry: Pick<AvailabilityEntry, 'blockType' | 'group' | 'client'> & {
		groupNumber?: number | null;
		clientNumber?: number | null;
	},
	scope: AvailabilityBlockType | LegacyBlockType | null | undefined,
	groupNumber?: number | null,
	clientNumber?: number | null
): boolean {
	const normalizedScope = normalizeBlockType(scope);
	const normalizedEntryType = normalizeBlockType(entry.blockType);

	if (!normalizedScope || !normalizedEntryType || normalizedScope !== normalizedEntryType) {
		return false;
	}

	if (normalizedScope === BLOCK_TYPES.AVAILABLE_CLIENT || normalizedScope === BLOCK_TYPES.BLOCK_CLIENT) {
		return Number(entry.client?.clientNumber ?? entry.clientNumber) === Number(clientNumber);
	}

	if (normalizedScope === BLOCK_TYPES.AVAILABLE_GROUP || normalizedScope === BLOCK_TYPES.BLOCK_GROUP) {
		return Number(entry.group?.groupNumber ?? entry.groupNumber) === Number(groupNumber);
	}

	return true;
}
